export type SessionAudience =
  | "public-embed"
  | "dashboard-customer"
  | `public-commerce:${string}`;

export interface SessionScope {
  orgId: number;
  audience: SessionAudience;
}

export interface ScopedSession {
  scope: SessionScope;
  token: string;
}

export interface SessionStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const SESSION_KEY_PREFIX = "zipper_embed_session:v2";

export function sessionStorageKey(scope: SessionScope): string {
  if (!Number.isInteger(scope.orgId) || scope.orgId <= 0) {
    throw new TypeError("A positive organization ID is required for session scope");
  }
  return `${SESSION_KEY_PREFIX}:${scope.audience}:${scope.orgId}`;
}

export class ScopedSessionStore {
  private readonly memory = new Map<string, string>();

  constructor(private readonly storage?: SessionStorageLike) {}

  read(scope: SessionScope): ScopedSession | null {
    const key = sessionStorageKey(scope);
    let token = this.memory.get(key);
    if (!token && this.storage) {
      try {
        token = this.storage.getItem(key) ?? undefined;
      } catch {
        // Storage is optional. The in-memory session remains authoritative.
      }
    }
    return token ? { scope, token } : null;
  }

  write(session: ScopedSession): void {
    const key = sessionStorageKey(session.scope);
    this.memory.set(key, session.token);
    if (!this.storage) return;
    try {
      this.storage.setItem(key, session.token);
    } catch {
      // Private mode/storage policy: retain an in-memory session.
    }
  }

  clear(scope: SessionScope): void {
    const key = sessionStorageKey(scope);
    this.memory.delete(key);
    if (!this.storage) return;
    try {
      this.storage.removeItem(key);
    } catch {
      // Clearing memory is sufficient when persistent storage is unavailable.
    }
  }
}
